import type { FrontFrame } from './frontFrame';
import type { Ring } from '../../../../geo/types';
import { toLayout } from '../planFrame';
import type { Landmark } from '../../../../data/types';
import { stadhuisFrameOf } from './frontFrame';

const DEG = Math.PI / 180;

/**
 * Reconstruction of the painted town hall: the vierschaar and tower retain their earlier sizes;
 * the narrow pale gabled house beside the tower runs back into the block under a dark slate roof.
 * The 6.5 m frontage, 54 m depth and lower rear ranges are estimates, not surveyed dimensions.
 */
export const STADHUIS = {
  /* Pin the existing court and tower to their former 40 m front; a reservation is not a scale. */
  front: { referenceLength: 40, vierschaar: 19 / 87, tower: 11 / 87 },
  /* The vierschaar stood almost 3 m beyond the building line, open on three sides so the public
     could follow the court (nl.wikipedia Oude Stadhuis; T. Bakker, Dam, p. 11). The footprint's
     front edge is its front; the tower and the hall stand on the line behind it. */
  projection: 3,
  courtDepth: 16,
  /* Heights in the painted proportion to the hall's eaves at 11 m (frame px over the foot at 563,
     stadhuis-scan.jpg): vierschaar front 31 px and its hip 44.5 against the eaves' 26, hall ridge
     50, tower shaft 58, lantern 67, spire 85, roof turret 64.6. */
  vierschaar: { eaveY: 13, apexY: 18.5 },
  /* Keep the painted height fit while narrowing the frontage; pitch follows those estimates. */
  hall: { width: 6.5, depth: 54, eaveY: 11, ridgeY: 21.2 },
  rearPitch: 50 * DEG,
  tower: { shaftY: 24.5, octagonY: 28.5, spireY: 36 },
  turret: { topY: 27.3, sink: 1, depthShare: 10.5 / 27 },
  /* Three tall pointed arches, 4.5 painted px wide in a 19 px bay, 16 px high; one more in each
     side of the projection. Walls 0.6 m thick, the loggia's vault just over the crowns. */
  arcade: { count: 3, pier: 0.8, height: 6.6, wall: 0.6, sideJamb: 0.3, ceiling: 0.4 },
} as const;

export interface PlanRect {
  readonly u0: number;
  readonly u1: number;
  readonly v0: number;
  readonly v1: number;
}

/** A pointed arch in its wall's panel, measured from the wall's start. */
export interface PointedArch {
  readonly center: number;
  readonly width: number;
  readonly spring: number;
}

export interface StadhuisPlan {
  readonly frame: FrontFrame;
  readonly vierschaar: PlanRect & {
    readonly eaveY: number;
    readonly ridgeY: number;
    readonly pitch: number;
  };
  /** The open ground floor of the projection, inside its walls, up to its ceiling. */
  readonly loggia: PlanRect & { readonly ceilingY: number };
  /** Along the vierschaar front from its south end. */
  readonly arcade: readonly PointedArch[];
  /** In each side of the projection, along v from the front. */
  readonly sideArch: PointedArch;
  readonly tower: PlanRect & {
    readonly shaftY: number;
    readonly octagonY: number;
    readonly spireY: number;
  };
  readonly hall: PlanRect & {
    readonly eaveY: number;
    readonly ridgeY: number;
    readonly pitch: number;
  };
  readonly frontGable: { readonly apexY: number };
  readonly turret: {
    readonly u: number;
    readonly v: number;
    readonly footY: number;
    readonly topY: number;
  };
  readonly backRanges: readonly (PlanRect & { readonly eaveY: number; readonly ridgeY: number })[];
}

/** An equilateral pointed arch of this width rises width * sin 60° over its springing. */
const POINTED_RISE = Math.sin(60 * DEG);

function arch(center: number, width: number): PointedArch {
  return { center, width, spring: STADHUIS.arcade.height - width * POINTED_RISE };
}

function arcadeOf(width: number): PointedArch[] {
  const { count, pier } = STADHUIS.arcade;
  const span = (width - (count + 1) * pier) / count;
  return Array.from({ length: count }, (_, k) => arch(pier + span / 2 + k * (span + pier), span));
}

function vierschaarOf(u1: number, depth: number): StadhuisPlan['vierschaar'] {
  const { eaveY, apexY } = STADHUIS.vierschaar;
  const pitch = Math.atan((apexY - eaveY) / (u1 / 2));
  return { u0: 0, u1, v0: 0, v1: depth, eaveY, ridgeY: apexY, pitch };
}

function loggiaOf(u1: number): { loggia: StadhuisPlan['loggia']; sideArch: PointedArch } {
  const { wall, sideJamb, height, ceiling } = STADHUIS.arcade;
  const line = STADHUIS.projection;
  const loggia = { u0: wall, u1: u1 - wall, v0: wall, v1: line, ceilingY: height + ceiling };
  return { loggia, sideArch: arch((wall + line) / 2, line - wall - 2 * sideJamb) };
}

export function planStadhuis(frame: FrontFrame): StadhuisPlan {
  const s = STADHUIS;
  const L = s.front.referenceLength;
  const line = s.projection;
  const back = line + s.courtDepth;
  const u1 = s.front.vierschaar * L;
  const t = s.front.tower * L;
  /* A 3 cm joint keeps the two closed masonry volumes from sharing coplanar party-wall faces. */
  const hallU0 = u1 + t + 0.03;
  const hallU1 = u1 + t + s.hall.width;
  const hallRidge = s.hall.ridgeY;
  const hallPitch = Math.atan((hallRidge - s.hall.eaveY) / ((hallU1 - hallU0) / 2));
  const rear = (u0: number, u1: number, v0: number, v1: number, eaveY: number) => ({
    u0,
    u1,
    v0,
    v1,
    eaveY,
    ridgeY: eaveY + ((u1 - u0) / 2) * Math.tan(s.rearPitch),
  });
  return {
    frame,
    vierschaar: vierschaarOf(u1, back),
    ...loggiaOf(u1),
    arcade: arcadeOf(u1),
    tower: { u0: u1, u1: u1 + t, v0: line, v1: line + t, ...s.tower },
    hall: {
      u0: hallU0,
      u1: hallU1,
      v0: line,
      v1: line + s.hall.depth,
      eaveY: s.hall.eaveY,
      ridgeY: hallRidge,
      pitch: hallPitch,
    },
    frontGable: { apexY: hallRidge },
    turret: {
      u: (hallU0 + hallU1) / 2,
      v: line + s.hall.depth * s.turret.depthShare,
      footY: hallRidge - s.turret.sink,
      topY: s.turret.topY,
    },
    /* The painting records a cluster, not its hidden plans. Keep these lower estimated ranges
       separate, with service passages between them, rather than inventing one full-width block. */
    backRanges: [
      rear(0.4, u1 - 0.6, back + 1, 29, 7),
      rear(u1 + 0.6, hallU0 - 1, line + t + 1, 19.1, 7.5),
      rear(u1 + 0.4, hallU0 - 1, 21, 27.8, 6.8),
    ],
  };
}

/** One deterministic plan shared by rendering and walk collision, without square context. */
export function stadhuisPlanOf(landmark: Pick<Landmark, 'footprint' | 'heading'>): StadhuisPlan {
  return planStadhuis(stadhuisFrameOf(landmark));
}

function rectRing(r: PlanRect): Ring {
  return [
    [r.u0, r.v0],
    [r.u1, r.v0],
    [r.u1, r.v1],
    [r.u0, r.v1],
  ];
}

/** Grounded volumes in layout coordinates. The estimated reservation must not close the alleys. */
export function stadhuisGroundedRings(plan: StadhuisPlan): Ring[] {
  const vs = plan.vierschaar;
  const wall = STADHUIS.arcade.wall;
  const piers: PlanRect[] = [];
  let start = vs.u0;
  for (const a of plan.arcade) {
    piers.push({ u0: start, u1: a.center - a.width / 2, v0: 0, v1: wall });
    start = a.center + a.width / 2;
  }
  piers.push({ u0: start, u1: vs.u1, v0: 0, v1: wall });
  const side = plan.sideArch;
  const frontJamb = side.center - side.width / 2;
  const backJamb = side.center + side.width / 2;
  for (const [u0, u1] of [
    [vs.u0, vs.u0 + wall],
    [vs.u1 - wall, vs.u1],
  ]) {
    piers.push({ u0: u0!, u1: u1!, v0: 0, v1: frontJamb });
    piers.push({ u0: u0!, u1: u1!, v0: backJamb, v1: plan.loggia.v1 });
  }
  const courtBody = { ...vs, v0: plan.loggia.v1 };
  return [courtBody, ...piers, plan.tower, plan.hall, ...plan.backRanges].map((r) =>
    rectRing(r).map((p) => toLayout(plan.frame.plan, p))
  );
}

/**
 * The ground the town hall stands on: the vierschaar with its loggia, the tower, the hall and the
 * back ranges. Their own walls go down into it where it falls away (mesh/footedBuilder.ts), and
 * the loggia's floor is the ground of the Dam. The rest of the reserved plot is yard and passage
 * and is left to the ground: one foundation under the whole plot stood as a platform 1.4 m above
 * the ground where it falls to the Nieuwezijds Voorburgwal, and one under each building as a band
 * of plain brick up to a metre high.
 */
export function stadhuisFoundations(landmark: Pick<Landmark, 'footprint' | 'heading'>): Ring[] {
  const plan = stadhuisPlanOf(landmark);
  return [plan.vierschaar, plan.tower, plan.hall, ...plan.backRanges].map((r) =>
    rectRing(r).map((p) => toLayout(plan.frame.plan, p))
  );
}
